import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../config/api';
import logger from '../utils/logger';

/**
 * Persona Context - Manages user's active persona (CO, PM, Hybrid)
 * Controls navigation visibility and role-specific features
 */
const PersonaContext = createContext();

export const usePersona = () => {
  const context = useContext(PersonaContext);
  if (!context) {
    throw new Error('usePersona must be used within a PersonaProvider');
  }
  return context;
};

// Persona configurations with full details
const PERSONA_CONFIGS = {
  co: {
    id: 'co',
    name: 'Contracting Officer',
    shortName: 'CO',
    icon: '',
    description: 'Solicitations, vendor evaluation, and contract management',
    color: 'blue',
    bgColor: 'bg-blue-500',
    textColor: 'text-blue-500',
    lightBg: 'bg-blue-100 dark:bg-blue-900/50',
    lightText: 'text-blue-700 dark:text-blue-300'
  },
  pm: {
    id: 'pm',
    name: 'Program Manager',
    shortName: 'PM',
    icon: '',
    description: 'Project oversight, requirements, and performance',
    color: 'green',
    bgColor: 'bg-green-500',
    textColor: 'text-green-500',
    lightBg: 'bg-green-100 dark:bg-green-900/50',
    lightText: 'text-green-700 dark:text-green-300'
  },
  hybrid: {
    id: 'hybrid',
    name: 'Hybrid',
    shortName: 'CO+PM',
    icon: '',
    description: 'Full access to contracting and program management',
    color: 'purple',
    bgColor: 'bg-purple-500',
    textColor: 'text-purple-500',
    lightBg: 'bg-purple-100 dark:bg-purple-900/50',
    lightText: 'text-purple-700 dark:text-purple-300'
  }
};

// Navigation items by persona
const NAV_ITEMS_BY_PERSONA = {
  co: [
    { path: '/', label: 'Dashboard', icon: 'home', section: 'main' },
    { path: '/solicitation-hub', label: 'Solicitation Hub', icon: 'clipboard', section: 'procurement', isNew: true },
    { path: '/vendor-database', label: 'Vendor Database', icon: 'building', section: 'procurement', isNew: true },
    { path: '/evaluation-workbench', label: 'Evaluation Workbench', icon: 'chart', section: 'procurement', isNew: true },
    { path: '/contracts', label: 'Contracts', icon: 'document', section: 'contracts' },
    { path: '/acquisition-timeline', label: 'Acquisition Timeline', icon: 'clock', section: 'timeline', isNew: true },
  ],
  pm: [
    { path: '/', label: 'Dashboard', icon: 'home', section: 'main' },
    { path: '/projects', label: 'Projects', icon: 'folder', section: 'projects' },
    { path: '/portfolio-radar', label: 'Portfolio Radar', icon: 'radar', section: 'projects' },
    { path: '/risks', label: 'Risk Dashboard', icon: 'warning', section: 'projects' },
    { path: '/modules', label: 'Modules & Domains', icon: 'cube', section: 'requirements' },
    { path: '/acquisition-timeline', label: 'Acquisition Timeline', icon: 'clock', section: 'timeline', isNew: true },
  ],
  hybrid: [
    { path: '/', label: 'Dashboard', icon: 'home', section: 'main' },
    { path: '/solicitation-hub', label: 'Solicitation Hub', icon: 'clipboard', section: 'procurement', isNew: true },
    { path: '/vendor-database', label: 'Vendor Database', icon: 'building', section: 'procurement', isNew: true },
    { path: '/evaluation-workbench', label: 'Evaluation Workbench', icon: 'chart', section: 'procurement', isNew: true },
    { path: '/projects', label: 'Projects', icon: 'folder', section: 'projects' },
    { path: '/contracts', label: 'Contracts', icon: 'document', section: 'contracts' },
    { path: '/acquisition-timeline', label: 'Acquisition Timeline', icon: 'clock', section: 'timeline', isNew: true },
    { path: '/portfolio-radar', label: 'Portfolio Radar', icon: 'radar', section: 'projects' },
  ]
};

export const PersonaProvider = ({ children }) => {
  const [activePersona, setActivePersona] = useState(() => {
    // Try to get from localStorage first
    const saved = localStorage.getItem('activePersona');
    return saved || 'hybrid';
  });
  const [personaConfig, setPersonaConfig] = useState(PERSONA_CONFIGS.hybrid);
  const [navItems, setNavItems] = useState(NAV_ITEMS_BY_PERSONA.hybrid);
  const [loading, setLoading] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check auth state
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsAuthenticated(!!token);
  }, []);

  // Fetch current persona from server on auth
  const fetchCurrentPersona = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await api.get('/persona/current', { requireAuth: true });
      if (response.success) {
        const persona = response.active_persona || 'hybrid';
        setActivePersona(persona);
        setPersonaConfig(PERSONA_CONFIGS[persona] || PERSONA_CONFIGS.hybrid);
        setNavItems(NAV_ITEMS_BY_PERSONA[persona] || NAV_ITEMS_BY_PERSONA.hybrid);
        localStorage.setItem('activePersona', persona);
      }
    } catch (err) {
      logger.error('Failed to fetch persona:', err);
      // Use localStorage value or default
    }
  }, []);

  useEffect(() => {
    fetchCurrentPersona();
  }, [fetchCurrentPersona]);

  // Update config and nav when persona changes
  useEffect(() => {
    setPersonaConfig(PERSONA_CONFIGS[activePersona] || PERSONA_CONFIGS.hybrid);
    setNavItems(NAV_ITEMS_BY_PERSONA[activePersona] || NAV_ITEMS_BY_PERSONA.hybrid);
  }, [activePersona]);

  // Switch persona
  const switchPersona = async (newPersona) => {
    if (!PERSONA_CONFIGS[newPersona]) return;
    
    setLoading(true);
    try {
      const response = await api.put('/persona/switch', { persona: newPersona }, { requireAuth: true });
      if (response.success) {
        setActivePersona(newPersona);
        localStorage.setItem('activePersona', newPersona);
        setSwitcherOpen(false);
        return { success: true, message: response.message };
      }
    } catch (err) {
      logger.error('Failed to switch persona:', err);
      return { success: false, error: 'Failed to switch persona' };
    } finally {
      setLoading(false);
    }
  };

  // Check if a feature/path is accessible for current persona
  const hasAccess = (path) => {
    return navItems.some(item => item.path === path);
  };

  // Check if user has specific permission
  const hasPermission = (permission) => {
    const permissions = {
      co: ['create_solicitation', 'post_solicitation', 'evaluate_vendors', 'award_contracts', 'manage_vendors', 'view_timeline'],
      pm: ['manage_projects', 'define_requirements', 'monitor_performance', 'view_contracts', 'view_timeline'],
      hybrid: ['create_solicitation', 'post_solicitation', 'evaluate_vendors', 'award_contracts', 'manage_vendors', 'manage_projects', 'define_requirements', 'monitor_performance', 'view_timeline']
    };
    return (permissions[activePersona] || []).includes(permission);
  };

  const value = {
    activePersona,
    personaConfig,
    navItems,
    allPersonas: Object.values(PERSONA_CONFIGS),
    loading,
    switcherOpen,
    setSwitcherOpen,
    switchPersona,
    hasAccess,
    hasPermission,
    PERSONA_CONFIGS
  };

  return (
    <PersonaContext.Provider value={value}>
      {children}
    </PersonaContext.Provider>
  );
};

export default PersonaContext;
